import usePrivateAxios from "@/common/api/usePrivateAxios"
import { getRailsUpdate } from "@/common/utils/getRailsUpdate"
import { transformNewProducts } from "@/common/utils/productTransformers"
import { mapToDeletion } from "@/common/utils/railsHelpers"
import { unFormatCnpj, unFormatCurrency, unFormatPercentage } from "@/common/utils/unFormatters"
import { EstablishmentLeadsFunnelUpdateContractValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { useMutation } from "@tanstack/react-query"

interface UseUpdateEstablishmentLeadContract {
  establishmentLeadId?: string,
  establishmentLead?: EstablishmentLeadData
  onSuccess?: () => void,
  onError?: (error: any) => void
}

export const useUpdateEstablishmentLeadContract = ({ establishmentLeadId, establishmentLead, onSuccess, onError }: UseUpdateEstablishmentLeadContract) => {
  const privateAxios = usePrivateAxios()
  return useMutation({
    mutationKey: ['updateEstablishmentLeadContract'],
    mutationFn: async (data: EstablishmentLeadsFunnelUpdateContractValidationSchema) => {
      const { maintenance_contract } = data;

      const lead = {
        contract_attributes: {
          ...data,
          bill_grouping_mode: data.bill_grouping_mode?.value || undefined,
          cycle: data.cycle?.value || undefined,
          cycle_start_day: data.cycle_start_day?.value ?? undefined,
          closing_to_due_in_days: data.closing_to_due_in_days?.value || undefined,
          due_to_suspension_in_days: data.due_to_suspension_in_days?.value || undefined,
          cnpj_for_fiscal_document: data.cnpj_for_fiscal_document ? unFormatCnpj(data.cnpj_for_fiscal_document) : undefined,
          payment_method: data.payment_method?.value || undefined,
          fiscal_document_requirement: data.fiscal_document_requirement?.value || undefined,
          contract_product_categories_attributes: getRailsUpdate(establishmentLead?.contract?.contract_product_categories || [], data?.contract_product_categories || [], (x) => x.product_category.id, (x) => x.value, mapToDeletion, transformNewProducts),
          contract_products_attributes: getRailsUpdate(establishmentLead?.contract?.contract_products || [], data?.contract_products || [], (x) => x.product.id, (x) => x.value, mapToDeletion, (prod: { value: string }) => ({ product_id: prod.value })),
          contract_fees_attributes: data.contract_fees?.map((contractFee) => ({
            id: contractFee.id,
            value: contractFee.fee_template.calculate_as === 'percentage'
              ? unFormatPercentage(contractFee.value)
              : unFormatCurrency(contractFee.value),
          })),
        },
        maintenance_contract_attributes: {
          ...maintenance_contract,
          cycle: maintenance_contract?.cycle?.value || undefined,
          cycle_start_day: maintenance_contract?.cycle_start_day?.value ?? undefined,
          closing_to_due_in_days: maintenance_contract?.closing_to_due_in_days?.value || undefined,
        },
      }

      await privateAxios.put(`/funnel/establishment/leads/${establishmentLeadId}`, {
        lead
      })
    },
    onSuccess,
    onError,
  })
}
